import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useApproveInspection, useRejectInspection } from '@/hooks/use-inspection-mutations';
import { formatTimestamp } from '@/lib/date';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ── Review action dialog ──────────────────────────────────────────────────────

interface ReviewActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  notes: string;
  onNotesChange: (v: string) => void;
  onConfirm: () => void;
  confirmLabel: string;
  confirmVariant?: 'default' | 'destructive';
  notesRequired?: boolean;
}

function ReviewActionDialog({
  open,
  onOpenChange,
  title,
  description,
  notes,
  onNotesChange,
  onConfirm,
  confirmLabel,
  confirmVariant = 'default',
  notesRequired = false,
}: ReviewActionDialogProps) {
  const isEmpty = notes.trim() === '';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label
              htmlFor="review-action-notes"
              className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
            >
              Notes for agent{notesRequired ? ' *' : ' (optional)'}
            </Label>
            <Textarea
              id="review-action-notes"
              placeholder={
                notesRequired
                  ? 'Required — explain what needs to be corrected…'
                  : 'Add notes for the agent…'
              }
              className="mt-2 min-h-[100px] rounded-xl border-border/40 text-sm"
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
            />
            {notesRequired && isEmpty && (
              <p className="mt-1.5 text-xs text-destructive">
                Notes are required to reject an inspection.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            className="font-bold"
            onClick={onConfirm}
            disabled={notesRequired && isEmpty}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review actions hook ───────────────────────────────────────────────────────

function useReviewActions(inspectionId: string, summary: string) {
  const navigate = useNavigate();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');

  const approveMutation = useApproveInspection();
  const rejectMutation = useRejectInspection();

  const handleApprove = () => {
    approveMutation.mutate(
      { id: inspectionId, request: { summary: summary.trim() || undefined } },
      { onSuccess: () => navigate({ to: '/dashboard' }) },
    );
  };

  const handleReject = () => {
    rejectMutation.mutate(
      {
        id: inspectionId,
        request: { reviewNotes: rejectNotes.trim(), summary: summary.trim() || undefined },
      },
      {
        onSuccess: () => {
          setRejectOpen(false);
          setRejectNotes('');
          navigate({ to: '/dashboard' });
        },
      },
    );
  };

  return { handleApprove, rejectOpen, setRejectOpen, rejectNotes, setRejectNotes, handleReject };
}

// ── Exports ───────────────────────────────────────────────────────────────────

export function ReviewActionsSection({
  inspectionId,
  summary,
}: {
  inspectionId: string;
  summary: string;
}) {
  const actions = useReviewActions(inspectionId, summary);
  return (
    <>
      <div className="flex gap-3 pt-2">
        <Button
          size="lg"
          variant="destructive"
          className="flex-1 h-12 font-semibold"
          onClick={() => actions.setRejectOpen(true)}
        >
          <XCircle className="mr-2 h-5 w-5" />
          Reject
        </Button>
        <Button
          size="lg"
          className="flex-1 h-12 font-semibold bg-emerald-600 hover:bg-emerald-700"
          onClick={actions.handleApprove}
        >
          <CheckCircle2 className="mr-2 h-5 w-5" />
          Approve
        </Button>
      </div>

      <ReviewActionDialog
        open={actions.rejectOpen}
        onOpenChange={actions.setRejectOpen}
        title="Reject Inspection"
        description="The inspection will be rejected. Explain what needs to be corrected so the agent can resubmit."
        notes={actions.rejectNotes}
        onNotesChange={actions.setRejectNotes}
        onConfirm={actions.handleReject}
        confirmLabel="Reject"
        confirmVariant="destructive"
        notesRequired
      />
    </>
  );
}

export function ReviewNotesSection({ notes, reviewedAt }: { notes: string; reviewedAt?: string }) {
  return (
    <section>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        Review Notes
      </p>
      <p className="text-sm text-muted-foreground leading-relaxed rounded-xl border border-border/40 bg-muted/30 p-4">
        {notes}
      </p>
      {reviewedAt && (
        <p className="text-xs text-muted-foreground/60 mt-1.5">
          Reviewed {formatTimestamp(reviewedAt, { dateStyle: 'long' })}
        </p>
      )}
    </section>
  );
}
